import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, formatINR, trackEvent, WHATSAPP_NUMBER } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import UploadDesignModal from "@/components/UploadDesignModal";
import Lightbox from "@/components/Lightbox";
import SwatchPickerModal from "@/components/SwatchPickerModal";
import { toast } from "sonner";
import { Plus, Maximize2, ArrowRight } from "lucide-react";

function SwatchPicker({ product }) {
  const [swatches, setSwatches] = useState([]);
  const [kind, setKind] = useState("fabric");
  const [selected, setSelected] = useState({});
  const { add } = useCart();

  useEffect(() => {
    axios.get(`${API}/products?category=swatches&limit=60`).then((r) => setSwatches(r.data)).catch(() => {});
  }, []);

  const filtered = swatches.filter((s) => s.swatch_kind === kind);
  const totalSelected = Object.values(selected).filter(Boolean).length;
  const perSwatch = filtered[0]?.starts_at || 50;
  const totalPrice = totalSelected * perSwatch;

  const toggle = (slug) => setSelected((prev) => ({ ...prev, [slug]: !prev[slug] }));

  const addAll = () => {
    const items = filtered.filter((s) => selected[s.slug]);
    if (!items.length) return;
    items.forEach((s) => {
      add({
        product_id: s.id,
        quantity: 1,
        customization: {},
        unit_price: s.starts_at || 50,
        name: s.name,
        image: s.images?.[0],
      });
    });
    toast.success(`${items.length} swatch${items.length > 1 ? "es" : ""} added to cart`);
    setSelected({});
  };

  if (product.category === "swatches") return null;

  return (
    <section className="mt-16 pt-10" style={{ borderTop: "1px solid var(--hairline)" }} data-testid="pdp-swatch-picker">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <div className="font-micro text-[#B8895C] mb-2">Sampled Before You Commit</div>
          <h3 className="font-serif text-[#EDE4D3] text-2xl md:text-3xl">Order Swatches &mdash; ₹50 each</h3>
          <p className="text-[#8A8680] text-sm mt-2 max-w-md leading-relaxed">10cm × 10cm samples of our fabrics and woods. Feel the grain and sheen before you commit to the piece.</p>
        </div>
        <Link to="/category/swatches" className="font-micro link-underline text-[#EDE4D3]" data-testid="pdp-swatch-view-all">Full Swatch Library →</Link>
      </div>

      <div className="mt-6 flex gap-2">
        <button className={`chip ${kind === "fabric" ? "active" : ""}`} onClick={() => setKind("fabric")} data-testid="pdp-swatch-kind-fabric">Fabric</button>
        <button className={`chip ${kind === "wood" ? "active" : ""}`} onClick={() => setKind("wood")} data-testid="pdp-swatch-kind-wood">Wood</button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="pdp-swatch-grid">
        {filtered.map((s) => {
          const on = !!selected[s.slug];
          return (
            <button
              key={s.slug}
              onClick={() => toggle(s.slug)}
              className={`text-left group transition-all duration-500 ${on ? "opacity-100" : "opacity-90 hover:opacity-100"}`}
              style={{ background: "#141414", border: `1px solid ${on ? "#B8895C" : "var(--hairline)"}` }}
              data-testid={`pdp-swatch-${s.slug}`}
            >
              <div className="aspect-square" style={{ background: s.swatch_hex || "#141414" }} />
              <div className="p-3">
                <div className="font-serif text-[#EDE4D3] text-sm leading-tight">{s.name.replace(" Swatch", "")}</div>
                <div className="flex justify-between items-center mt-2">
                  <div className="font-micro text-[#8A8680]">{formatINR(s.starts_at || 50)}</div>
                  <div className={`font-micro ${on ? "text-[#B8895C]" : "text-[#8A8680]"}`}>{on ? "✓ Added" : "+ Add"}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="font-micro text-[#8A8680]" data-testid="pdp-swatch-summary">
          {totalSelected > 0 ? `${totalSelected} selected · ${formatINR(totalPrice)}` : "Select the swatches you'd like to sample"}
        </div>
        <button
          className="btn-bronze"
          disabled={totalSelected === 0}
          onClick={addAll}
          style={totalSelected === 0 ? { opacity: 0.4, cursor: "not-allowed" } : {}}
          data-testid="pdp-swatch-add-to-cart"
        >
          <Plus size={12} className="inline mr-2" /> Add {totalSelected || ""} Swatch{totalSelected === 1 ? "" : "es"} to Cart
        </button>
      </div>
    </section>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selection, setSelection] = useState({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [swatchModalKind, setSwatchModalKind] = useState(null); // "fabric" | "wood" | null
  const { add } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/${slug}`).then((r) => {
      setProduct(r.data);
      const c = r.data.customization || {};
      const initial = {};
      Object.keys(c).forEach((k) => { initial[k] = c[k]?.[0]?.name || ""; });
      setSelection(initial);
      setImgIdx(0);
    });
  }, [slug]);

  const priceDelta = useMemo(() => {
    if (!product) return 0;
    const c = product.customization || {};
    return Object.keys(c).reduce((sum, k) => {
      const found = (c[k] || []).find((o) => o.name === selection[k]);
      return sum + (found?.price_delta || 0);
    }, 0);
  }, [product, selection]);

  if (!product) return <div className="text-[#8A8680] font-micro py-24 text-center">Loading…</div>;

  const price = product.starts_at + priceDelta;
  const discount = product.compare_at ? Math.round((1 - product.starts_at / product.compare_at) * 100) : 0;
  const wa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi Teak Studio — enquiring about ${product.name} (${window.location.href})`)}`;

  const onAdd = () => {
    add({
      product_id: product.id,
      quantity: 1,
      customization: selection,
      unit_price: price,
      name: product.name,
      image: product.images?.[0],
    });
    toast.success("Added to cart");
  };

  const isSwatch = product.category === "swatches";

  return (
    <div className="fade-in max-w-[1440px] mx-auto px-6 md:px-12 py-10" data-testid="product-detail">
      <div className="grid md:grid-cols-[1.4fr_1fr] gap-12">
        <div>
          <div
            className="img-cinematic aspect-[4/5] relative cursor-zoom-in group"
            style={{ background: product.swatch_hex || "#141414" }}
            onClick={() => product.images?.length && setLightboxOpen(true)}
            data-testid="pdp-main-image"
            role="button"
            aria-label="View full-screen"
          >
            {product.images?.[imgIdx] && (
              <img src={product.images[imgIdx]} alt={product.name} className="w-full h-full object-cover" />
            )}
            <div
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-[#0B0B0C]/70 text-[#EDE4D3] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ border: "1px solid var(--hairline)" }}
              data-testid="pdp-zoom-hint"
            >
              <Maximize2 size={14} strokeWidth={1.25} />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            {product.images?.map((im, i) => (
              <button
                key={i}
                onClick={() => setImgIdx(i)}
                onDoubleClick={() => { setImgIdx(i); setLightboxOpen(true); }}
                className={`w-20 h-24 img-cinematic transition-opacity duration-500 ${i === imgIdx ? "opacity-100" : "opacity-50"}`}
                style={{ border: i === imgIdx ? "1px solid #B8895C" : "1px solid var(--hairline)" }}
                data-testid={`gallery-thumb-${i}`}>
                <img src={im} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="md:sticky md:top-32 md:self-start">
          {!isSwatch && <span className="ribbon-bronze mb-6">Direct from Factory</span>}
          {isSwatch && <span className="ribbon-bronze mb-6">Sample Swatch</span>}
          <div className="font-micro text-[#8A8680] mt-6 mb-2">{product.category?.replace("-", " ")} · {product.sub_category}</div>
          <h1 className="font-serif text-[#EDE4D3] text-4xl md:text-5xl leading-tight" data-testid="pdp-title">{product.name}</h1>
          <p className="text-[#8A8680] mt-5 leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-baseline gap-4">
            <div className="font-micro text-[#8A8680]">{isSwatch ? "Price" : "Starts at"}</div>
            <div className="font-serif text-[#EDE4D3] text-3xl" data-testid="pdp-price">{formatINR(price)}</div>
            {product.compare_at && (
              <div className="font-micro text-[#8A8680] line-through">{formatINR(product.compare_at + priceDelta)}</div>
            )}
            {discount > 0 && <div className="font-micro text-[#B8895C]">−{discount}%</div>}
          </div>

          <div className="mt-10 space-y-8">
            {Object.entries(product.customization || {}).map(([key, opts]) => {
              const swatchKind =
                key === "fabric" ? "fabric" :
                (key === "wood" || key === "wood_finish") ? "wood" : null;
              return (
                <div key={key} data-testid={`customize-${key}`}>
                  <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
                    <div className="font-micro text-[#8A8680]">{key.replace("_", " ")}</div>
                    {swatchKind && (
                      <button
                        onClick={() => setSwatchModalKind(swatchKind)}
                        className="font-micro text-[#B8895C] hover:text-[#EDE4D3] transition-colors duration-500 flex items-center gap-1.5"
                        data-testid={`open-swatch-modal-${swatchKind}`}
                      >
                        Order {swatchKind} swatches — ₹50 each <ArrowRight size={10} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {opts.map((o) => (
                      <button key={o.name}
                        onClick={() => setSelection({ ...selection, [key]: o.name })}
                        className={`chip ${selection[key] === o.name ? "active" : ""}`}
                        data-testid={`customize-${key}-${o.name.replace(/\s+/g, "-").toLowerCase()}`}>
                        <span className="flex items-center gap-2">
                          {o.hex && <span className="inline-block w-3 h-3" style={{ background: o.hex }} />}
                          {o.name}
                          {o.price_delta > 0 && <span className="text-[#8A8680] ml-1">+{formatINR(o.price_delta)}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 flex flex-col gap-4">
            <button className="btn-bronze" onClick={onAdd} data-testid="pdp-add-to-cart">Add to Cart · {formatINR(price)}</button>
            <div className="grid grid-cols-2 gap-3">
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { product_id: product.id })} className="btn-ghost text-center" data-testid="pdp-whatsapp">WhatsApp</a>
              {!isSwatch && (
                <button onClick={() => setUploadOpen(true)} className="btn-ghost" data-testid="pdp-customise-this">Customise this Product</button>
              )}
            </div>
          </div>

          <div className="mt-10 space-y-3 text-sm text-[#8A8680] font-micro">
            <div className="flex justify-between py-3" style={{ borderTop: "1px solid var(--hairline)" }}>
              <span>Lead Time</span><span>{isSwatch ? "3–5 days" : "4–6 Weeks"}</span>
            </div>
            <div className="flex justify-between py-3" style={{ borderTop: "1px solid var(--hairline)" }}>
              <span>Warranty</span><span>{isSwatch ? "N/A" : "10 Years on Frame"}</span>
            </div>
            <div className="flex justify-between py-3" style={{ borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)" }}>
              <span>Delivery</span><span>Pan India</span>
            </div>
          </div>
        </div>
      </div>

      <SwatchPicker product={product} />

      <UploadDesignModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        productContext={{ name: product.name, slug: product.slug, url: window.location.href }}
      />

      {lightboxOpen && (
        <Lightbox
          images={product.images || []}
          index={imgIdx}
          onIndexChange={setImgIdx}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <SwatchPickerModal
        open={!!swatchModalKind}
        kind={swatchModalKind}
        productName={product.name}
        onClose={() => setSwatchModalKind(null)}
      />
    </div>
  );
}
